import { Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, CheckCircle2, AlertCircle, Stamp, GitBranchPlus, Cloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PdfActions } from "./PdfActions";

function SaveStatus({ saveState, readOnly }) {
  if (readOnly) return <span className="text-xs text-slate-500 inline-flex items-center gap-1" data-testid="qg-save-status"><Stamp className="h-3.5 w-3.5" />Issued — locked</span>;
  const at = saveState.at ? new Date(saveState.at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";
  const map = {
    saving: [<Loader2 key="i" className="h-3.5 w-3.5 animate-spin" />, "Saving…", "text-slate-500"],
    saved: [<CheckCircle2 key="i" className="h-3.5 w-3.5" />, `Saved${at ? ` ${at}` : ""}`, "text-emerald-700"],
    dirty: [<Cloud key="i" className="h-3.5 w-3.5" />, "Unsaved changes", "text-amber-700"],
    error: [<AlertCircle key="i" className="h-3.5 w-3.5" />, saveState.error || "Save failed", "text-red-600"],
    idle: [null, "", "text-slate-500"],
  };
  const [icon, label, cls] = map[saveState.status] || map.idle;
  return <span className={`text-xs inline-flex items-center gap-1 max-w-[260px] truncate ${cls}`} data-testid="qg-save-status" title={label}>{icon}{label}</span>;
}

export function EditorHeader({ q, readOnly, saveState, onSave, onFinalize, onNewRevision, busy, pdfRevision, setPdfRevision, beforePdf }) {
  const issued = q.issued_revisions || [];
  const pdfValue = pdfRevision === null || pdfRevision === undefined ? "current" : String(pdfRevision);
  const pdfName = `${q.quotation_number}_${pdfValue === "current" ? q.version_label : `R${pdfRevision}`}${pdfValue === "current" && !readOnly ? "_DRAFT" : ""}`;
  return (
    <div className="sticky top-0 z-30 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200 dark:border-slate-700 px-6 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/quotation-generator" className="text-slate-500 hover:text-brillien-600" data-testid="qg-back-link" title="All quotations"><ArrowLeft className="h-5 w-5" /></Link>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="font-mono font-bold text-slate-900 dark:text-white truncate" data-testid="qg-editor-number">{q.quotation_number}</h1>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${readOnly ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid="qg-editor-status">
              {readOnly ? `Issued ${q.version_label}` : `Draft ${q.version_label}`}
            </span>
          </div>
          <p className="text-xs text-slate-500 truncate">{q.client_name} · {q.project_name}</p>
        </div>
        <SaveStatus saveState={saveState} readOnly={readOnly} />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {issued.length > 0 && (
            <Select value={pdfValue} onValueChange={(v) => setPdfRevision(v === "current" ? null : Number(v))}>
              <SelectTrigger className="h-9 w-44 rounded-lg text-xs" data-testid="qg-pdf-revision-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="current">{readOnly ? `Current (${q.version_label})` : `Current draft (${q.version_label})`}</SelectItem>
                {issued.map((r) => <SelectItem key={r.revision_number} value={String(r.revision_number)}>Issued {r.version_label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <PdfActions quotationId={q.id} fileName={pdfName} revision={pdfRevision} beforeFetch={beforePdf} testPrefix="qg-editor-pdf" />
          {!readOnly && (
            <Button variant="outline" onClick={onSave} disabled={saveState.status === "saving"} className="rounded-lg" data-testid="qg-save-draft-btn">
              <Save className="h-4 w-4 mr-1.5" />Save draft
            </Button>
          )}
          {readOnly ? (
            <Button onClick={onNewRevision} disabled={busy} className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white" data-testid="qg-new-revision-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <GitBranchPlus className="h-4 w-4 mr-1.5" />}Edit as R{(q.revision_number || 0) + 1}
            </Button>
          ) : (
            <Button onClick={onFinalize} disabled={busy} className="rounded-lg bg-brillien-600 hover:bg-brillien-700 text-white" data-testid="qg-finalize-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Stamp className="h-4 w-4 mr-1.5" />}Generate final
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
